import {
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  Subject,
  EMPTY,
  catchError,
  debounceTime,
  distinctUntilChanged,
  merge,
  of,
  switchMap,
  takeUntil,
} from 'rxjs';
import { DepositService } from 'src/app/pages/deposit/services/deposit.service';
import { DepositProductService } from 'src/app/pages/settings/services/deposit-products.service';
import { NotificationService } from 'src/app/utils/notification.service';
import { WizerValidatorService } from 'src/app/utils/wizer.validators.service';
import { PermissionService } from 'src/app/utils/permission.service';
import { Permissions } from 'src/app/shared/dto/permissions';
import { CurrencyFormatterService } from '../../services/currency-formatter.service';
import {
  LoadingService,
  QuietScope,
} from '../../services/loading.service';
import { DepositProductFeeCharge } from '../../models/deposit-product.model';
import { SearchableSelectTypes } from '../../models/common';

/** One row of the "Fees & Charges" grid, flattened for the template. */
interface DepositFeeView {
  name: string;
  value: string;
  minimum: string;
  maximum: string;
  event: string;
  eventType: 'DAILY' | 'QUARTERLY';
  eventTime: string;
  quarters: string[];
}

interface RateBand {
  min: number;
  max: number;
}

interface RecurringProjectionResult {
  numberOfContributionPeriods: number | null;
  contributionPerPeriod: number | null;
  totalContributions: number | null;
  projectedInterestEarned: number | null;
  completionBonus: number | null;
  projectedBalanceAtCompletion: number | null;
}

interface FixedDepositProjectionResult {
  numberOfCompoundingPeriods: number | null;
  durationInDays: number | null;
  maturityDate: string | null;
  interestToMaturity: number | null;
  maturityValue: number | null;
  earlyWithdrawalInterest: number | null;
  earlyWithdrawalPenalty: number | null;
  earlyWithdrawalPayout: number | null;
}

interface DepositAccountProjectionResult {
  projectedInterest: number | null;
}

/**
 * TEMPORARY FIGMA PREVIEW DATA.
 *
 * The savings-product API does not yet return these fields — it has no rate /
 * amount / duration bands, no dormancy threshold, no overdraft-specific
 * period units, and none of the fixed-deposit or recurring detail. Until it
 * does, the dialog shows the values from the Figma frames so the whole layout
 * is reviewable end to end.
 *
 * Every placeholder is reached through `preview()` below, so removing this
 * constant and that helper is all it takes to switch fully to live data.
 */
const PREVIEW = {
  // Deposit Account
  minimumBalance: 10000000,
  interestRate: 10,
  interestRateBand: { min: 0, max: 25 },
  overdraftLimit: 10000000,
  overdraftInterestRate: 10,
  transferLimit: 10000000,
  dailyTransferLimit: 10000000,
  dormancyThreshold: 30,
  roundingMultiple: 1,
  collectionAccounts: ['Sterling Bank', 'GT Bank', 'Wema Bank'],
  weekendDays: ['SATURDAY', 'SUNDAY'],
  enableWebClients: true,
  enableMobileClients: true,
  enableUssdClients: false,
  // Fixed Deposit / Recurring shared
  amountBand: { min: 20000, max: 1000000 },
  durationBand: { min: 90, max: 240 },
  amount: 150000,
  durationDays: 120,
  durationMonths: 120,
  // Fixed Deposit
  rolloverBasis: 'PRINCIPAL_AND_INTEREST',
  gracePeriod: 30,
  minimumHoldingPeriod: 30,
  penaltyOnInterest: 10,
  noticePeriod: 20,
  // Recurring Savings
  savingsFrequency: 'MONTH',
  dayOfMonth: 3,
  contributionSource: 'AUTO_DEBIT_LINKED_ACCOUNT',
  recurringGracePeriod: 2,
  percentageType: 'PERCENTAGE',
  onePercent: 1,
  minimumLockPeriod: 30,
  atCompletion: 'TRANSFER_TO_LINKED_SAVINGS_ACCOUNT',
  // Fees & charges
  fees: [
    { name: 'Processing Fee', amount: 5, event: 'DAILY' },
    { name: 'Admin. Fee', amount: 5, event: 'QUARTERLY' },
    { name: 'Search Fee', amount: 5, event: 'DAILY' },
  ],
};

/** Periods per year, used by every projection below. */
const PERIODS_PER_YEAR: Record<string, number> = {
  DAY: 365,
  WEEK: 52,
  MONTH: 12,
  YEAR: 1,
};

@Component({
  selector: 'app-new-deposit-account',
  templateUrl: './new-deposit-account.component.html',
  styleUrls: ['./new-deposit-account.component.css'],
})
export class NewDepositAccountComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private quietScope: QuietScope;

  public form: FormGroup;
  public isCorporate: boolean = false;

  /** Product-reference accordions start closed and retain their existing
      expand/collapse interaction once opened. */
  public otherInfoExpanded = false;
  public feesExpanded = false;

  /** Every approved savings product, kept so Type can re-filter Product. */
  private allProducts: any[] = [];
  public depositProducts: SearchableSelectTypes[] = [];
  /** Full record of the selected product; drives every read-only field. */
  public productRecord: any = null;

  public readonly productTypeOptions = [
    {
      value: 'DEPOSIT_ACCOUNT',
      label: $localize`:@@deposit_account:Deposit Account`,
    },
    {
      value: 'FIXED_DEPOSIT',
      label: $localize`:@@fixed_deposit_investment_account:Fixed Deposit/Investment Account`,
    },
    {
      value: 'RECURRING_SAVINGS',
      label: $localize`:@@recurring_targeted_savings:Recurring/Targeted Savings`,
    },
  ];

  public readonly periodUnitOptions = ['DAY', 'WEEK', 'MONTH', 'YEAR'];
  public readonly payoutPeriodUnitOptions = ['DAY', 'WEEK', 'MONTH', 'YEAR', 'TENOR'];
  public readonly weekendDayOptions = [
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
    'SUNDAY',
  ];
  public collectionAccountOptions: Array<{ value: any; label: string }> = [];
  public readonly channelDisplays: Array<{
    label: string;
    icon: string;
    control: string;
  }> = [
    {
      label: $localize`:@@web_app:Web App`,
      icon: 'assets/icons/webAppIcon.svg',
      control: 'enableWebClientsOverride',
    },
    {
      label: $localize`:@@mobile_app:Mobile App`,
      icon: 'assets/icons/mobileAppIcon.svg',
      control: 'enableMobileClientsOverride',
    },
    {
      label: $localize`:@@ussd:USSD`,
      icon: 'assets/icons/mobileAppIcon.svg',
      control: 'enableUssdClientsOverride',
    },
  ];

  private readonly destroy$ = new Subject<void>();
  private readonly fixedCalculatorRefresh$ = new Subject<void>();
  private readonly recurringCalculatorRefresh$ = new Subject<void>();
  private readonly depositCalculatorRefresh$ = new Subject<void>();
  public fixedProjectionResult: FixedDepositProjectionResult | null = null;
  public recurringProjectionResult: RecurringProjectionResult | null = null;
  public depositProjectionResult: DepositAccountProjectionResult | null = null;

  constructor(
    private fb: FormBuilder,
    private validatorService: WizerValidatorService,
    private service: DepositService,
    private notif: NotificationService,
    private depositProductService: DepositProductService,
    public currencyFormatter: CurrencyFormatterService,
    private permissionService: PermissionService,
    private dialogRef: MatDialogRef<NewDepositAccountComponent>,
    loading: LoadingService,

    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    /* The form is usable as soon as it opens, so its start-up requests run
       without the full-screen loader covering it. */
    this.quietScope = loading.beginQuietScope();
    if (data.clientData?.accountType) {
      this.isCorporate = data.clientData?.accountType
        .toLowerCase()
        .includes('corporate');
    }
  }

  ngOnInit(): void {
    this.initForm();
    this.wireEarlyWithdrawalGate();
    this.wireFixedDepositGates();
    this.loadDepositProducts();
    this.watchProductSelection();
    this.setupFixedDepositCalculator();
    this.setupRecurringCalculator();
    this.setupDepositAccountCalculator();
  }

  ngAfterViewInit(): void {
    // Start-up requests are under way; the scope ends when they finish.
    this.quietScope.settle();
  }

  /* Allow Early Withdrawal's three fields are real controls, so switching the
     section off has to disable them — the .fields-off class only stops the
     pointer, and Tab would still reach them. This is the only gated block
     with editable fields; the rest are read-only displays. */
  private wireEarlyWithdrawalGate(): void {
    const toggle = this.form.get('allowEarlyWithdrawal');
    if (!toggle) return;

    this.syncEarlyWithdrawalControls();
    toggle.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncEarlyWithdrawalControls());
  }

  private syncEarlyWithdrawalControls(): void {
    const enabled = this.booleanValue(
      this.form?.get('allowEarlyWithdrawal')?.value,
    ) && this.canAdjust('allowEarlyWithdrawal');
    [
      'minimumLockPeriodDays',
      'earlyWithdrawalPenaltyType',
      'earlyWithdrawalPenaltyValue',
    ].forEach((name) => {
      const control = this.form?.get(name);
      if (!control) return;
      enabled
        ? control.enable({ emitEvent: false })
        : control.disable({ emitEvent: false });
    });
  }

  private wireFixedDepositGates(): void {
    this.form.get('autoRollover')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncFixedDepositControls());
    this.form.get('allowPrematureWithdrawal')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncFixedDepositControls());
    this.syncFixedDepositControls();
  }

  private syncFixedDepositControls(): void {
    const maturityEnabled =
      this.isFixedDeposit &&
      this.booleanValue(this.form.get('autoRollover')?.value) &&
      this.canAdjust('autoRollover');
    ['fixedDepositRolloverBasis', 'fixedDepositRolloverGracePeriodDays']
      .forEach((name) => {
        const control = this.form.get(name);
        if (!control) return;
        maturityEnabled
          ? control.enable({ emitEvent: false })
          : control.disable({ emitEvent: false });
      });

    const withdrawalEnabled =
      this.isFixedDeposit &&
      this.booleanValue(this.form.get('allowPrematureWithdrawal')?.value) &&
      this.canAdjust('allowPrematureWithdrawal');
    [
      'fixedDepositMinimumHoldingPeriodDays',
      'fixedDepositPrematureWithdrawalPenaltyOnInterest',
      'fixedDepositPrematureWithdrawalNoticePeriodDays',
    ].forEach((name) => {
      const control = this.form.get(name);
      if (!control) return;
      withdrawalEnabled
        ? control.enable({ emitEvent: false })
        : control.disable({ emitEvent: false });
    });
  }

  ngOnDestroy(): void {
    this.quietScope.close();
    this.destroy$.next();
    this.destroy$.complete();
    this.fixedCalculatorRefresh$.complete();
    this.recurringCalculatorRefresh$.complete();
    this.depositCalculatorRefresh$.complete();
  }

  private initForm() {
    this.form = this.fb.group({
      productType: new FormControl('DEPOSIT_ACCOUNT', [Validators.required]),
      savingsProductId: new FormControl('', [Validators.required]),
      description: new FormControl({ value: '', disabled: true }),

      // Shared by all three types - start locked, open via permission + product.
      interestRate: new FormControl({ value: '', disabled: true }),
      interestRatePeriodUnit: new FormControl({ value: 'MONTH', disabled: true }),

      // Deposit Account only.
      allowOverdraft: new FormControl({ value: false, disabled: true }),
      overdraftInterestRate: new FormControl({ value: '', disabled: true }),
      overdraftInterestRatePeriodUnit: new FormControl({ value: 'MONTH', disabled: true }),
      overdraftLimit: new FormControl({ value: '', disabled: true }),
      overdraftInterestCalculation: new FormControl({ value: '', disabled: true }),
      overdraftCompoundingPeriodUnit: new FormControl({ value: 'MONTH', disabled: true }),
      averageBalance: new FormControl({ value: '', disabled: true }),
      depositInterestCalculation: new FormControl({ value: '', disabled: true }),
      depositCompoundingPeriodUnit: new FormControl({ value: 'MONTH', disabled: true }),
      depositPostingPeriodUnit: new FormControl({ value: 'MONTH', disabled: true }),
      overdraftPostingPeriodUnit: new FormControl({ value: 'MONTH', disabled: true }),
      calcAverageBalance: new FormControl(''),

      // Shared product information and channels.
      transferLimitOverride: new FormControl({ value: '', disabled: true }),
      dailyTransferLimitOverride: new FormControl({ value: '', disabled: true }),
      dormancyThresholdOverride: new FormControl({ value: '', disabled: true }),
      roundingMultipleOverride: new FormControl({ value: '', disabled: true }),
      collectionAccountsOverride: new FormControl({ value: [], disabled: true }),
      calculationBasisOverride: new FormControl({ value: 'WORKING', disabled: true }),
      weekendDaysOverride: new FormControl({ value: [], disabled: true }),
      enableWebClientsOverride: new FormControl({ value: false, disabled: true }),
      enableMobileClientsOverride: new FormControl({ value: false, disabled: true }),
      enableUssdClientsOverride: new FormControl({ value: false, disabled: true }),

      // Fixed Deposit only.
      amount: new FormControl({ value: '', disabled: true }),
      durationLength: new FormControl({ value: '', disabled: true }),
      durationUnit: new FormControl({ value: 'DAY', disabled: true }),
      fixedDepCompoundingPeriodUnit: new FormControl({ value: 'MONTH', disabled: true }),
      fixedDepositInterestPayoutPeriodUnit: new FormControl({ value: 'TENOR', disabled: true }),
      autoRollover: new FormControl({ value: false, disabled: true }),
      fixedDepositRolloverBasis: new FormControl({ value: '', disabled: true }),
      fixedDepositRolloverGracePeriodDays: new FormControl({ value: '', disabled: true }),
      allowPrematureWithdrawal: new FormControl({ value: false, disabled: true }),
      fixedDepositMinimumHoldingPeriodDays: new FormControl({ value: '', disabled: true }),
      fixedDepositPrematureWithdrawalPenaltyOnInterest: new FormControl({ value: '', disabled: true }),
      fixedDepositPrematureWithdrawalNoticePeriodDays: new FormControl({ value: '', disabled: true }),
      calcFixedAmount: new FormControl(''),

      // Recurring/Targeted Savings only.
      targetAmount: new FormControl({ value: '', disabled: true }),
      recurringDurationLength: new FormControl({ value: '', disabled: true }),
      recurringDurationUnit: new FormControl({ value: 'DAY', disabled: true }),
      recurringCompoundingPeriodUnit: new FormControl({ value: 'MONTH', disabled: true }),
      recurringFrequencyUnit: new FormControl({ value: 'MONTH', disabled: true }),
      recurringDayOfMonth: new FormControl({ value: '', disabled: true }),
      recurringContributionSource: new FormControl({ value: '', disabled: true }),
      recurringGracePeriod: new FormControl({ value: '', disabled: true }),
      recurringBonus: new FormControl({ value: '', disabled: true }),
      missedContributionPenaltyType: new FormControl({ value: 'PERCENTAGE', disabled: true }),
      missedContributionPenaltyValue: new FormControl({ value: '', disabled: true }),
      allowEarlyWithdrawal: new FormControl({ value: false, disabled: true }),
      // Allow Early Withdrawal's fields are editable, matching how
      // new-view-deposit-product does it.
      minimumLockPeriodDays: new FormControl(''),
      earlyWithdrawalPenaltyType: new FormControl('PERCENTAGE'),
      earlyWithdrawalPenaltyValue: new FormControl(''),
      forfeitBonusOnEarlyWithdrawal: new FormControl({ value: false, disabled: true }),
      forfeitBonusOnMissedContribution: new FormControl({ value: false, disabled: true }),
      recurringAtCompletion: new FormControl({ value: '', disabled: true }),
      // The recurring calculator takes its own three inputs.
      calcTargetAmount: new FormControl(''),
      calcDurationMonths: new FormControl(''),
      calcInterestRate: new FormControl(''),
    });

    this.applyTypeControlState();
  }

  /* Whether a section's switch is on, for gating the fields beneath it.
     Nothing here enables or disables form controls: the only real control in
     a gated block is overdraftInterestRate, and syncOverdraftControls()
     already owns that (it also gates on isDepositAccount, which this cannot
     express). Every other gated field is a read-only display with no control
     to disable, so the template handles them with [class.fields-off]. */
  public isSectionOn(gate: string): boolean {
    return !!this.form?.get(gate)?.value;
  }

  get savingsFormCtrl() {
    return this.form.get('savingsProductId') as FormControl;
  }

  // ── Product type ──────────────────────────────────────────────────────────

  public get productType(): string {
    return this.form?.get('productType')?.value;
  }

  public get isDepositAccount(): boolean {
    return this.productType === 'DEPOSIT_ACCOUNT';
  }

  public get isFixedDeposit(): boolean {
    return this.productType === 'FIXED_DEPOSIT';
  }

  public get isRecurringSavings(): boolean {
    return this.productType === 'RECURRING_SAVINGS';
  }

  /**
   * Disables fields belonging to inactive product types. Permission checks
   * will then enable the active type's fields for users with permission.
   */
  private applyTypeControlState(): void {
    const groups: Record<string, string[]> = {
      DEPOSIT_ACCOUNT: [
        'averageBalance',
        'depositInterestCalculation',
        'depositCompoundingPeriodUnit',
        'depositPostingPeriodUnit',
        'overdraftPostingPeriodUnit',
      ],
      FIXED_DEPOSIT: [
        'amount',
        'durationLength',
        'durationUnit',
        'fixedDepCompoundingPeriodUnit',
        'fixedDepositInterestPayoutPeriodUnit',
      ],
      RECURRING_SAVINGS: [
        'targetAmount',
        'recurringDurationLength',
        'recurringDurationUnit',
        'recurringCompoundingPeriodUnit',
        'recurringFrequencyUnit',
        'recurringDayOfMonth',
        'recurringContributionSource',
        'recurringGracePeriod',
        'recurringBonus',
        'missedContributionPenaltyType',
        'missedContributionPenaltyValue',
        'recurringAtCompletion',
      ],
    };

    // Disable all type-specific fields first
    Object.values(groups)
      .flat()
      .forEach((name) => {
        this.form.get(name)?.disable({ emitEvent: false });
      });

    this.applyFieldPermissions();
    // Calculator inputs are simulations, not account-term overrides. They
    // must remain available regardless of the user's term-edit permissions.
    [
      'calcAverageBalance',
      'calcFixedAmount',
      'calcTargetAmount',
      'calcDurationMonths',
      'calcInterestRate',
    ].forEach((name) => this.form.get(name)?.enable({ emitEvent: false }));
    // Apply the feature gate after permissions so an allowed field still
    // remains disabled while Allow Overdraft itself is off.
    this.syncOverdraftControls();
    this.syncFixedDepositControls();
  }

  // ── Field-level permissions ─────────────────────────────────────────────

  /* The product's terms a user may change on this account, each opened by its
     own permission (mirrors new-loan-account's ADJUSTABLE_TERMS). Fields with
     no entry here — e.g. the early-withdrawal lock period/penalty — stay
     editable for everyone, as before. overdraftInterestRate and its unit are
     looked up from here via canAdjust(), but enabled/disabled from
     syncOverdraftControls() rather than applyFieldPermissions() below, since
     they also gate on allowOverdraft. */
  private static readonly ADJUSTABLE_TERMS: { [control: string]: Permissions } =
    {
      description: Permissions.EDIT_DESCRIPTION,
      allowOverdraft: Permissions.EDIT_ALLOW_OVERDRAFT,
      overdraftInterestRate: Permissions.EDIT_OVERDRAFT_INTEREST_RATE,
      overdraftInterestRatePeriodUnit:
        Permissions.EDIT_OVERDRAFT_INTEREST_RATE_UNIT,
      overdraftLimit: Permissions.EDIT_OVERDRAFT_LIMIT,
      overdraftInterestCalculation: Permissions.EDIT_OVERDRAFT_INTEREST_CALCULATION,
      overdraftCompoundingPeriodUnit: Permissions.EDIT_OVERDRAFT_COMPOUNDING_PERIOD_UNIT,
      averageBalance: Permissions.EDIT_DEPOSIT_MINIMUM_BALANCE,
      depositInterestCalculation: Permissions.EDIT_DEPOSIT_INTEREST_CALCULATION,
      depositCompoundingPeriodUnit: Permissions.EDIT_DEPOSIT_COMPOUNDING_PERIOD_UNIT,
      depositPostingPeriodUnit: Permissions.CREATE_CLIENT_SAVINGS_ACCOUNT,
      overdraftPostingPeriodUnit: Permissions.CREATE_CLIENT_SAVINGS_ACCOUNT,
      amount: Permissions.EDIT_FIXED_DEP_AMOUNT,
      durationLength: Permissions.EDIT_FIXED_DEP_DURATION,
      durationUnit: Permissions.EDIT_FIXED_DEP_DURATION_UNIT,
      fixedDepCompoundingPeriodUnit: Permissions.EDIT_FIXED_DEP_COMPOUNDING_PERIOD_UNIT,
      fixedDepositInterestPayoutPeriodUnit: Permissions.EDIT_FIXED_DEP_INTEREST_RATE_UNIT,
      autoRollover: Permissions.EDIT_FIXED_DEP_ALLOW_AUTO_ROLLOVER,
      fixedDepositRolloverBasis: Permissions.EDIT_FIXED_DEP_ALLOW_AUTO_ROLLOVER,
      fixedDepositRolloverGracePeriodDays: Permissions.EDIT_FIXED_DEP_ALLOW_AUTO_ROLLOVER,
      allowPrematureWithdrawal:
        Permissions.EDIT_FIXED_DEP_ALLOW_PREMATURE_WITHDRAWAL,
      fixedDepositMinimumHoldingPeriodDays:
        Permissions.EDIT_FIXED_DEP_ALLOW_PREMATURE_WITHDRAWAL,
      fixedDepositPrematureWithdrawalPenaltyOnInterest:
        Permissions.EDIT_FIXED_DEP_ALLOW_PREMATURE_WITHDRAWAL,
      fixedDepositPrematureWithdrawalNoticePeriodDays:
        Permissions.EDIT_FIXED_DEP_ALLOW_PREMATURE_WITHDRAWAL,
      targetAmount: Permissions.EDIT_TARGET_SAVINGS_AMOUNT,
      recurringDurationLength: Permissions.EDIT_TARGET_SAVINGS_DURATION,
      recurringDurationUnit: Permissions.EDIT_TARGET_SAVINGS_DURATION_UNIT,
      recurringCompoundingPeriodUnit: Permissions.EDIT_TARGET_SAVINGS_COMPOUNDING_PERIOD_UNIT,
      recurringFrequencyUnit: Permissions.EDIT_TARGET_SAVINGS_FREQUENCY_UNIT,
      recurringDayOfMonth: Permissions.EDIT_TARGET_SAVINGS_DAY_OF_MONTH,
      recurringContributionSource: Permissions.EDIT_TARGET_SAVINGS_SOURCE,
      recurringGracePeriod: Permissions.EDIT_TARGET_SAVINGS_GRACE_PERIOD,
      recurringBonus: Permissions.EDIT_TARGET_SAVINGS_BONUS,
      missedContributionPenaltyType:
        Permissions.EDIT_TARGET_SAVINGS_MISSED_PENALTY,
      missedContributionPenaltyValue:
        Permissions.EDIT_TARGET_SAVINGS_MISSED_PENALTY,
      allowEarlyWithdrawal:
        Permissions.EDIT_TARGET_SAVINGS_ALLOW_EARLY_WITHDRAWAL,
      forfeitBonusOnEarlyWithdrawal: Permissions.EDIT_TARGET_SAVINGS_ALLOW_FORFEIT_BONUS,
      forfeitBonusOnMissedContribution:
        Permissions.EDIT_TARGET_SAVINGS_ALLOW_FORFEIT_BONUS,
      recurringAtCompletion: Permissions.EDIT_TARGET_SAVINGS_AT_COMPLETION,
    };

  /* Product configuration is shown for context on a new account. It is not an
     account-level override, so permissions (including super-admin) must never
     make these controls interactive. */
  private static readonly READ_ONLY_PRODUCT_INFORMATION = [
    'transferLimitOverride',
    'dailyTransferLimitOverride',
    'dormancyThresholdOverride',
    'roundingMultipleOverride',
    'collectionAccountsOverride',
    'calculationBasisOverride',
    'weekendDaysOverride',
    'enableWebClientsOverride',
    'enableMobileClientsOverride',
    'enableUssdClientsOverride',
  ];

  /* interestRate/interestRatePeriodUnit are the one control reused across all
     three product type sections, so the permission that opens them depends on
     whichever type is currently active. */
  private static readonly INTEREST_RATE_PERMISSION_BY_TYPE: {
    [type: string]: Permissions;
  } = {
    DEPOSIT_ACCOUNT: Permissions.EDIT_DEPOSIT_INTEREST_RATE,
    FIXED_DEPOSIT: Permissions.EDIT_FIXED_DEP_INTEREST_RATE,
    RECURRING_SAVINGS: Permissions.EDIT_TARGET_SAVINGS_INTEREST_RATE,
  };
  private static readonly INTEREST_RATE_UNIT_PERMISSION_BY_TYPE: {
    [type: string]: Permissions;
  } = {
    DEPOSIT_ACCOUNT: Permissions.EDIT_DEPOSIT_INTEREST_RATE_UNIT,
    FIXED_DEPOSIT: Permissions.EDIT_FIXED_DEP_INTEREST_RATE_UNIT,
    RECURRING_SAVINGS: Permissions.EDIT_TARGET_SAVINGS_INTEREST_RATE_UNIT,
  };

  /* Open only to a user holding the field's permission, and only once a
     product has supplied the figure. Type-specific fields also check they
     belong to the active type. Super users bypass the permission check —
     hasPermissionList() already special-cases them. */
  private canAdjust(control: string): boolean {
    const product = this.productRecord;
    if (!product) return false;

    // Type-specific fields: only adjustable if active type matches
    const typeSpecificFields = {
      DEPOSIT_ACCOUNT: [
        'averageBalance',
        'depositInterestCalculation',
        'depositCompoundingPeriodUnit',
        'depositPostingPeriodUnit',
        'overdraftPostingPeriodUnit',
        'allowOverdraft',
        'overdraftLimit',
        'overdraftInterestRate',
        'overdraftInterestRatePeriodUnit',
        'overdraftInterestCalculation',
        'overdraftCompoundingPeriodUnit',
      ],
      FIXED_DEPOSIT: [
        'amount',
        'durationLength',
        'durationUnit',
        'fixedDepCompoundingPeriodUnit',
        'fixedDepositInterestPayoutPeriodUnit',
        'autoRollover',
        'fixedDepositRolloverBasis',
        'fixedDepositRolloverGracePeriodDays',
        'allowPrematureWithdrawal',
        'fixedDepositMinimumHoldingPeriodDays',
        'fixedDepositPrematureWithdrawalPenaltyOnInterest',
        'fixedDepositPrematureWithdrawalNoticePeriodDays',
      ],
      RECURRING_SAVINGS: [
        'targetAmount',
        'recurringDurationLength',
        'recurringDurationUnit',
        'recurringCompoundingPeriodUnit',
        'recurringFrequencyUnit',
        'recurringDayOfMonth',
        'recurringContributionSource',
        'recurringGracePeriod',
        'recurringBonus',
        'missedContributionPenaltyType',
        'missedContributionPenaltyValue',
        'recurringAtCompletion',
        'allowEarlyWithdrawal',
        'forfeitBonusOnEarlyWithdrawal',
        'forfeitBonusOnMissedContribution',
      ],
    };

    // Check if field belongs to wrong type
    for (const [type, fields] of Object.entries(typeSpecificFields)) {
      if (fields.includes(control) && type !== this.productType) {
        return false;
      }
    }

    if (control === 'interestRate' || control === 'interestRatePeriodUnit') {
      const byType =
        control === 'interestRate'
          ? NewDepositAccountComponent.INTEREST_RATE_PERMISSION_BY_TYPE
          : NewDepositAccountComponent.INTEREST_RATE_UNIT_PERMISSION_BY_TYPE;
      const permission = byType[this.productType];
      return (
        !!permission && this.permissionService.hasPermissionList([permission])
      );
    }

    const permission = NewDepositAccountComponent.ADJUSTABLE_TERMS[control];
    if (!permission) return true;
    return this.permissionService.hasPermissionList([permission]);
  }

  /* Opens each field for which the user has permission and a product has been
     selected. Fields belonging to a different product type (already disabled by
     applyTypeControlState) are skipped. overdraftInterestRate and its unit are
     handled by syncOverdraftControls since they also gate on allowOverdraft. */
  private applyFieldPermissions(): void {
    Object.keys(NewDepositAccountComponent.ADJUSTABLE_TERMS).forEach(
      (name) => {
        const control = this.form.get(name);
        if (!control) return;
        if (this.canAdjust(name)) control.enable({ emitEvent: false });
        else control.disable({ emitEvent: false });
      },
    );

    ['interestRate', 'interestRatePeriodUnit'].forEach((name) => {
      const control = this.form.get(name);
      if (!control) return;
      if (this.canAdjust(name)) control.enable({ emitEvent: false });
      else control.disable({ emitEvent: false });
    });

    NewDepositAccountComponent.READ_ONLY_PRODUCT_INFORMATION.forEach((name) =>
      this.form.get(name)?.disable({ emitEvent: false }),
    );
  }

  // ── Products ──────────────────────────────────────────────────────────────

  private loadDepositProducts(): void {
    const requestedType = this.savingsProductListType;
    this.depositProductService
      .getAllRecordsUnpaged(requestedType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (requestedType !== this.savingsProductListType) return;
          const products = Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response)
              ? response
              : [];
          this.allProducts = products;
          this.updateProductOptions();
        },
        error: () => {
          this.allProducts = [];
          this.updateProductOptions();
        },
      });
  }

  private updateProductOptions(): void {
    this.depositProducts = this.allProducts
      .map((product) => ({ label: product?.name, value: product?.id }));
  }

  private get savingsProductListType(): string {
    return this.productType === 'RECURRING_SAVINGS'
      ? 'RECURRING_TARGETED'
      : this.productType;
  }

  public onProductTypeChange(): void {
    this.form.get('savingsProductId').reset('');
    this.productRecord = null;
    this.collectionAccountOptions = [];
    this.allProducts = [];
    this.depositProducts = [];
    this.applyTypeControlState();
    this.loadDepositProducts();
  }

  private watchProductSelection(): void {
    this.form
      .get('savingsProductId')
      .valueChanges.pipe(
        // distinctUntilChanged sits first so the reset to '' on a Type change
        // clears its memory; otherwise re-picking the same product afterwards
        // would be swallowed and leave the read-only fields empty.
        distinctUntilChanged(),
        switchMap((id) => {
          // The unpaged response is only used to build the Product options.
          // Clear the previous details immediately, then load the selected
          // product's complete configuration from the single-record endpoint.
          this.productRecord = null;
          this.collectionAccountOptions = [];
          this.form.get('description').setValue('', { emitEvent: false });
          if (!id) return of(null);

          return this.depositProductService.getRecord(Number(id)).pipe(
            catchError(() => of(null)),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((response: any) => {
        const record = response?.data ?? response;
        if (!record) {
          this.productRecord = null;
          return;
        }

        this.applyProductRecord(record);
      });
  }

  /**
   * Read the first field that the payload actually carries. The list and
   * single-product endpoints name the same data differently (openingBalance vs
   * minimumOpeningBalance, postingPeriodUnit vs interestPostingPeriodUnit), so
   * every read goes through a fallback chain.
   */
  private field(...keys: string[]): any {
    const product = this.productRecord;
    if (!product) return undefined;
    for (const key of keys) {
      const value = product[key];
      if (value !== null && value !== undefined && value !== '') return value;
    }
    return undefined;
  }

  /** Fall back only when the API omitted a value. Zero is a valid configured
      amount/percentage and must be rendered as returned. */
  private preview<T>(value: any, placeholder: T): any {
    if (value === null || value === undefined || value === '') {
      return placeholder;
    }
    return value;
  }

  private booleanValue(value: any, fallback = false): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', '1', 'yes'].includes(normalized)) return true;
      if (['false', '0', 'no'].includes(normalized)) return false;
    }
    return fallback;
  }

  private collectionAccountValues(record: any): any[] {
    const accounts = record?.collectionAccounts
      ?? record?.fundSources
      ?? record?.savingsReference
      ?? record?.savingsReferences
      ?? [];
    if (!Array.isArray(accounts)) return [];
    return accounts.map((account: any) =>
      account?.accountId
      ?? account?.glAccountId
      ?? account?.id
      ?? account?.accountName
      ?? account?.glAccountName
      ?? account?.name,
    ).filter((value: any) => value !== null && value !== undefined && value !== '');
  }

  private applyProductRecord(record: any): void {
    this.productRecord = record || null;
    this.collectionAccountOptions = this.buildCollectionAccountOptions(record);
    const rate = this.preview(
      (this.isRecurringSavings
        ? record?.recurringDefaultInterestRate
        : this.isFixedDeposit
          ? record?.fixedDepositDefaultInterestRate
          : undefined) ??
        record?.defaultInterestRate ??
        record?.interestRate,
      PREVIEW.interestRate,
    );

    this.form.patchValue(
      {
        description: record?.description ?? '',
        interestRate: rate,
        interestRatePeriodUnit:
          record?.recurringInterestPeriodUnit ||
          record?.fixedDepositInterestPeriodUnit ||
          record?.interestPeriodUnit ||
          record?.interestRatePeriodUnit ||
          'MONTH',

        overdraftInterestRate: this.preview(
          record?.overdraftInterestRate,
          PREVIEW.overdraftInterestRate,
        ),
        overdraftInterestRatePeriodUnit:
          record?.overdraftInterestPeriodUnit ||
          record?.interestRatePeriodUnit ||
          'MONTH',
        overdraftLimit: this.preview(
          record?.overdraftLimit,
          PREVIEW.overdraftLimit,
        ),
        overdraftInterestCalculation:
          record?.overdraftInterestCalculationType ||
          record?.interestCalculationType ||
          'AVERAGE_BALANCE',
        overdraftCompoundingPeriodUnit:
          record?.overdraftCompoundingPeriodUnit ||
          record?.compoundingPeriodUnit ||
          'MONTH',
        depositInterestCalculation:
          record?.interestCalculationType || 'AVERAGE_BALANCE',
        depositCompoundingPeriodUnit:
          record?.compoundingPeriodUnit ||
          record?.compoundingFrequency ||
          'MONTH',
        depositPostingPeriodUnit:
          record?.interestPostingPeriodUnit ||
          record?.postingPeriodUnit ||
          'MONTH',
        overdraftPostingPeriodUnit:
          record?.overdraftInterestPostingPeriodUnit ||
          record?.interestPostingPeriodUnit ||
          'MONTH',
        averageBalance: this.preview(
          record?.minimumBalance ??
          record?.minimumOpeningBalance ??
          record?.openingBalance,
          PREVIEW.minimumBalance,
        ),
        calcAverageBalance: this.preview(
          record?.minimumBalance ??
          record?.minimumOpeningBalance ??
          record?.openingBalance,
          PREVIEW.minimumBalance,
        ),
        transferLimitOverride: record?.transferLimit ?? '',
        dailyTransferLimitOverride: record?.dailyTransferLimit ?? '',
        dormancyThresholdOverride: record?.dormancyThreshold ?? '',
        roundingMultipleOverride: record?.roundingMultiple ?? '',
        collectionAccountsOverride: this.collectionAccountValues(record),
        calculationBasisOverride:
          (record?.isCalenderDays ?? record?.isCalendarDays)
            ? 'CALENDAR'
            : 'WORKING',
        weekendDaysOverride: Array.isArray(record?.weekendDays)
          ? record.weekendDays
          : [],
        enableWebClientsOverride: this.booleanValue(record?.enableWebClients),
        enableMobileClientsOverride: this.booleanValue(record?.enableMobileClients),
        enableUssdClientsOverride: this.booleanValue(record?.enableUssdClients),
        allowOverdraft: Boolean(
          record?.allowOverdraft ?? record?.overdraftLimit,
        ),

        amount: this.preview(
          record?.fixedDepositDefaultAmount ?? record?.defaultAmount,
          PREVIEW.amount,
        ),
        calcFixedAmount: this.preview(
          record?.fixedDepositDefaultAmount ?? record?.defaultAmount,
          PREVIEW.amount,
        ),
        durationLength: this.preview(
          record?.fixedDepositDefaultDuration ?? record?.defaultDurationDays,
          PREVIEW.durationDays,
        ),
        durationUnit: record?.fixedDepositDurationPeriodUnit || 'DAY',
        fixedDepCompoundingPeriodUnit:
          record?.fixedDepositCompoundingPeriodUnit ||
          record?.compoundingPeriodUnit ||
          'MONTH',
        fixedDepositInterestPayoutPeriodUnit:
          record?.fixedDepositInterestPayoutPeriodUnit ||
          record?.interestPayoutPeriodUnit ||
          record?.interestPayout ||
          'TENOR',
        autoRollover: Boolean(
          record?.fixedDepositAutoRolloverAtMaturity ?? record?.autoRollover,
        ),
        fixedDepositRolloverBasis:
          record?.fixedDepositRolloverBasis ?? record?.rolloverBasis ?? '',
        fixedDepositRolloverGracePeriodDays:
          record?.fixedDepositRolloverGracePeriodDays ?? record?.gracePeriod ?? '',
        allowPrematureWithdrawal: Boolean(
          record?.fixedDepositAllowPrematureWithdrawal ??
          record?.allowPrematureWithdrawal,
        ),
        fixedDepositMinimumHoldingPeriodDays:
          record?.fixedDepositMinimumHoldingPeriodDays ??
          record?.minimumHoldingPeriod ?? '',
        fixedDepositPrematureWithdrawalPenaltyOnInterest:
          record?.fixedDepositPrematureWithdrawalPenaltyOnInterest ??
          record?.penaltyOnInterest ?? '',
        fixedDepositPrematureWithdrawalNoticePeriodDays:
          record?.fixedDepositPrematureWithdrawalNoticePeriodDays ??
          record?.noticePeriod ?? '',

        targetAmount: this.preview(
          record?.recurringDefaultTargetAmount ?? record?.defaultTargetAmount,
          PREVIEW.amount,
        ),
        recurringDurationLength: this.preview(
          record?.recurringDefaultDuration ?? record?.defaultDurationMonths,
          PREVIEW.durationMonths,
        ),
        recurringDurationUnit:
          record?.recurringDurationPeriodUnit || 'MONTH',
        recurringCompoundingPeriodUnit:
          record?.recurringCompoundingPeriodUnit ||
          record?.compoundingPeriodUnit ||
          'MONTH',
        recurringFrequencyUnit:
          record?.recurringSavingsFrequency ||
          record?.savingsFrequency ||
          'MONTH',
        recurringDayOfMonth: this.preview(
          record?.recurringDayOfMonth ||
            record?.savingsDayOfMonth ||
            record?.dayOfMonth ||
            record?.contributionDayOfMonth,
          PREVIEW.dayOfMonth,
        ),
        recurringContributionSource:
          this.normalizeRecurringContributionSource(
            record?.recurringContributionSource ||
            record?.contributionSource ||
            'AUTO_DEBIT_LINKED_ACCOUNT',
          ),
        recurringGracePeriod: this.preview(
          record?.recurringGracePeriodDays || record?.gracePeriod,
          PREVIEW.recurringGracePeriod,
        ),
        recurringBonus: this.preview(
          record?.recurringBonusValue || record?.bonusValue,
          PREVIEW.onePercent,
        ),
        missedContributionPenaltyType:
          record?.recurringMissedContributionPenaltyType ||
          record?.missedContributionPenaltyType ||
          'PERCENTAGE',
        missedContributionPenaltyValue: this.preview(
          record?.recurringMissedContributionPenaltyValue ??
            record?.missedContributionPenaltyValue,
          PREVIEW.onePercent,
        ),
        recurringAtCompletion:
          this.normalizeRecurringCompletionAction(
            record?.recurringAtCompletionAction ||
            record?.glTemplate ||
            'TRANSFER_TO_LINKED_SAVINGS_ACCOUNT',
          ),
        allowEarlyWithdrawal: this.booleanValue(
          record?.recurringAllowEarlyWithdrawal ?? record?.allowEarlyWithdrawal,
        ),
        minimumLockPeriodDays: this.preview(
          record?.recurringEarlyWithdrawalMinimumLockPeriodDays ??
            record?.minimumLockPeriodDays ??
            record?.minimumLockPeriod ??
            record?.maximumSavedPeriodDays,
          PREVIEW.minimumLockPeriod,
        ),
        earlyWithdrawalPenaltyType: this.preview(
          record?.recurringEarlyWithdrawalPenaltyType ??
            record?.earlyWithdrawalPenaltyType,
          PREVIEW.percentageType,
        ),
        earlyWithdrawalPenaltyValue: this.preview(
          record?.recurringEarlyWithdrawalPenaltyValue ??
            record?.earlyWithdrawalPenaltyValue,
          PREVIEW.onePercent,
        ),
        forfeitBonusOnEarlyWithdrawal: this.booleanValue(
          record?.recurringForfeitBonusOnEarlyWithdrawal ??
          record?.forfeitBonusOnEarlyWithdrawal,
        ),
        forfeitBonusOnMissedContribution: this.booleanValue(
          record?.recurringForfeitBonusOnMissedContribution ??
          record?.forfeitBonusOnMissedContribution,
        ),
        calcTargetAmount: this.preview(
          record?.recurringDefaultTargetAmount ?? record?.defaultTargetAmount,
          PREVIEW.amount,
        ),
        calcDurationMonths: this.preview(
          record?.recurringDefaultDuration ?? record?.defaultDurationMonths,
          PREVIEW.durationMonths,
        ),
        calcInterestRate: rate,
      },
      { emitEvent: false },
    );

    this.applyBandValidators();
    this.applyTypeControlState();
    this.syncFixedDepositControls();
    this.syncEarlyWithdrawalControls();
    this.fixedCalculatorRefresh$.next();
    this.recurringCalculatorRefresh$.next();
    this.depositCalculatorRefresh$.next();
  }

  private setupFixedDepositCalculator(): void {
    merge(
      this.form.get('savingsProductId').valueChanges,
      this.form.get('calcFixedAmount').valueChanges,
      this.fixedCalculatorRefresh$,
    )
      .pipe(
        debounceTime(700),
        switchMap(() => {
          const savingsProductId = Number(this.form.get('savingsProductId')?.value);
          const amount = this.toNumber(this.form.get('calcFixedAmount')?.value);

          if (!this.isFixedDeposit || !savingsProductId || amount <= 0) {
            this.fixedProjectionResult = null;
            return EMPTY;
          }

          const payload = { savingsProductId, amount };

          return this.service.calculateFixedDepositProjection(payload).pipe(
            catchError((error) => {
              this.fixedProjectionResult = null;
              return EMPTY;
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((response: any) => {
        this.fixedProjectionResult = response?.successful === false
          ? null
          : response?.data ?? null;
      });
  }

  private setupRecurringCalculator(): void {
    const savingsProductChanges = this.form.get('savingsProductId').valueChanges;
    const targetAmountChanges = this.form.get('calcTargetAmount').valueChanges;
    const durationChanges = this.form.get('calcDurationMonths').valueChanges;
    const interestRateChanges = this.form.get('calcInterestRate').valueChanges;

    merge(
      savingsProductChanges,
      targetAmountChanges,
      durationChanges,
      interestRateChanges,
      this.recurringCalculatorRefresh$,
    )
      .pipe(
        debounceTime(700),
        switchMap(() => {
          const savingsProductId = Number(this.form.get('savingsProductId')?.value);
          const targetAmount = this.toNumber(this.form.get('calcTargetAmount')?.value);
          const duration = this.toNumber(this.form.get('calcDurationMonths')?.value);
          const interestRate = this.toNumber(this.form.get('calcInterestRate')?.value);

          if (
            !this.isRecurringSavings ||
            !savingsProductId ||
            targetAmount <= 0 ||
            duration <= 0 ||
            interestRate <= 0
          ) {
            this.recurringProjectionResult = null;
            return EMPTY;
          }

          const payload = {
            savingsProductId,
            targetAmount,
            duration,
            interestRate,
          };

          return this.service.calculateRecurringTargetedProjection(payload).pipe(
            catchError((error) => {
              this.recurringProjectionResult = null;
              return EMPTY;
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((response: any) => {
        this.recurringProjectionResult = response?.successful === false
          ? null
          : response?.data ?? null;
      });
  }

  private setupDepositAccountCalculator(): void {
    merge(
      this.form.get('savingsProductId').valueChanges,
      this.form.get('calcAverageBalance').valueChanges,
      this.depositCalculatorRefresh$,
    )
      .pipe(
        debounceTime(700),
        switchMap(() => {
          const savingsProductId = Number(this.form.get('savingsProductId')?.value);
          const averageBalance = this.toNumber(this.form.get('calcAverageBalance')?.value);

          if (!this.isDepositAccount || !savingsProductId || averageBalance <= 0) {
            this.depositProjectionResult = null;
            return EMPTY;
          }

          const payload = { savingsProductId, averageBalance };

          return this.service.calculateDepositAccountProjection(payload).pipe(
            catchError((error) => {
              this.depositProjectionResult = null;
              return EMPTY;
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((response: any) => {
        this.depositProjectionResult = response?.successful === false
          ? null
          : response?.data ?? null;
      });
  }

  // ── Bands and their "Range: …" subtexts ───────────────────────────────────

  /**
   * The permitted band for an input, or null when the product declares none.
   * Only FIXED_DEPOSIT and RECURRING_SAVINGS carry minimum/maximum values; a
   * plain DEPOSIT_ACCOUNT product has single fixed figures instead.
   */
  private band(minKeys: string[], maxKeys: string[]): RateBand | null {
    const product = this.productRecord;
    if (!product) return null;

    const pick = (keys: string[]) => {
      for (const key of keys) {
        const value = product[key];
        if (value !== null && value !== undefined && value !== '') {
          return Number(value);
        }
      }
      return null;
    };

    const min = pick(minKeys);
    const max = pick(maxKeys);
    if (min === null || max === null) return null;
    if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
    return { min, max };
  }

  public get interestRateBand(): RateBand | null {
    const minKeys = this.isRecurringSavings
      ? ['recurringMinimumInterestRate', 'minimumInterestRate', 'minInterestRate']
      : this.isFixedDeposit
        ? ['fixedDepositMinimumInterestRate', 'minimumInterestRate', 'minInterestRate']
        : ['minimumInterestRate', 'minInterestRate'];
    const maxKeys = this.isRecurringSavings
      ? ['recurringMaximumInterestRate', 'maximumInterestRate', 'maxInterestRate']
      : this.isFixedDeposit
        ? ['fixedDepositMaximumInterestRate', 'maximumInterestRate', 'maxInterestRate']
        : ['maximumInterestRate', 'maxInterestRate'];
    return (
      this.band(minKeys, maxKeys) ?? PREVIEW.interestRateBand
    );
  }

  public get overdraftInterestRateBand(): RateBand | null {
    return (
      this.band(
        ['minimumOverdraftInterestRate', 'overdraftMinimumInterestRate'],
        ['maximumOverdraftInterestRate', 'overdraftMaximumInterestRate'],
      ) ?? PREVIEW.interestRateBand
    );
  }

  public get amountBand(): RateBand | null {
    const band = this.isRecurringSavings
      ? this.band(
          ['recurringMinimumTargetAmount', 'minimumTargetAmount'],
          ['recurringMaximumTargetAmount', 'maximumTargetAmount'],
        )
      : this.isFixedDeposit
        ? this.band(
            ['fixedDepositMinimumAmount', 'minimumAmount'],
            ['fixedDepositMaximumAmount', 'maximumAmount'],
          )
        : this.band(['minimumAmount'], ['maximumAmount']);
    return band ?? PREVIEW.amountBand;
  }

  public get durationBand(): RateBand | null {
    const band = this.isRecurringSavings
      ? this.band(
          ['recurringMinimumDuration', 'minimumDurationMonths'],
          ['recurringMaximumDuration', 'maximumDurationMonths'],
        )
      : this.isFixedDeposit
        ? this.band(
            ['fixedDepositMinimumDuration', 'minimumDurationDays'],
            ['fixedDepositMaximumDuration', 'maximumDurationDays'],
          )
        : this.band(['minimumDurationDays'], ['maximumDurationDays']);
    return band ?? PREVIEW.durationBand;
  }

  private rangeText(band: RateBand, format: (value: number) => string): string {
    return (
      $localize`:@@rangeHint:Range` +
      `: ${format(band.min)} - ${format(band.max)}`
    );
  }

  /** "Range: 5% - 25%" under each Interest Rate field. */
  public get interestRateHint(): string {
    const band = this.interestRateBand;
    if (band) return this.rangeText(band, (value) => `${value}%`);
    if (!this.productRecord) return '';
    const rate = this.toNumber(
      this.productRecord?.interestRate ??
        this.productRecord?.defaultInterestRate,
    );
    return $localize`:@@productRateHint:Product rate` + `: ${rate}%`;
  }

  public get overdraftInterestRateHint(): string {
    const band = this.overdraftInterestRateBand;
    if (band) return this.rangeText(band, (value) => `${value}%`);
    if (!this.productRecord) return '';
    const rate = this.toNumber(this.productRecord?.overdraftInterestRate);
    return $localize`:@@productRateHint:Product rate` + `: ${rate}%`;
  }

  /** "Range: 20,000 - 1,000,000" under Amount / Target Amount. */
  public get amountHint(): string {
    const band = this.amountBand;
    if (!band) return '';
    return this.rangeText(band, (value) =>
      value.toLocaleString(undefined, { maximumFractionDigits: 0 }),
    );
  }

  /** "Range: 90 - 240" under Duration. */
  public get durationHint(): string {
    const band = this.durationBand;
    if (!band) return '';
    return this.rangeText(band, (value) => String(value));
  }

  private applyBandValidators(): void {
    // Calculator values are simulations only. They stay editable for every
    // user and must never prevent creation of an otherwise valid account,
    // especially when another account type's calculator is hidden.
    [
      'calcAverageBalance',
      'calcFixedAmount',
      'calcTargetAmount',
      'calcDurationMonths',
      'calcInterestRate',
    ].forEach((name) => {
      const control = this.form.get(name);
      control?.clearValidators();
      control?.updateValueAndValidity({ emitEvent: false });
    });

    const bindRate = (controlName: string, band: RateBand | null) => {
      const control = this.form.get(controlName);
      const validators = [Validators.pattern(/^(\d{1,2}(\.\d{1,2})?|100(\.0{1,2})?)$/), Validators.min(0)];
      if (band) {
        validators.push(Validators.min(band.min), Validators.max(band.max));
      }
      control.setValidators(validators);
      control.updateValueAndValidity({ emitEvent: false });
    };

    const bindNumber = (
      controlName: string,
      band: RateBand | null,
      required: boolean,
    ) => {
      const control = this.form.get(controlName);
      const validators: ValidatorFn[] = required ? [Validators.required] : [];
      if (band) {
        validators.push(this.numericRangeValidator(band));
      }
      control.setValidators(validators);
      control.updateValueAndValidity({ emitEvent: false });
    };

    bindRate('interestRate', this.interestRateBand);
    bindRate('overdraftInterestRate', this.overdraftInterestRateBand);

    // Amount and Duration are required in both variant designs (marked *).
    bindNumber('amount', this.amountBand, true);
    bindNumber('durationLength', this.durationBand, true);
    bindNumber('targetAmount', this.amountBand, true);
    bindNumber('recurringDurationLength', this.durationBand, true);
  }

  /** Angular's min/max validators parse "260,000.00" as 260. Amount fields
      are comma-formatted on blur, so validate them with the same numeric
      normalization used to build calculator and submission payloads. */
  private numericRangeValidator(band: RateBand): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        control.value === null ||
        control.value === undefined ||
        control.value === ''
      ) {
        return null;
      }

      const value = this.toNumber(control.value);
      if (value < band.min) {
        return { min: { min: band.min, actual: value } };
      }
      if (value > band.max) {
        return { max: { max: band.max, actual: value } };
      }
      return null;
    };
  }

  // ── Overdraft (Deposit Account) ───────────────────────────────────────────

  public get allowOverdraft(): boolean {
    return Boolean(this.form?.get('allowOverdraft')?.value);
  }

  public onAllowOverdraftChange(): void {
    this.syncOverdraftControls();
  }

  private syncOverdraftControls(): void {
    const rate = this.form.get('overdraftInterestRate');
    const unit = this.form.get('overdraftInterestRatePeriodUnit');
    const limit = this.form.get('overdraftLimit');
    const calculation = this.form.get('overdraftInterestCalculation');
    const compounding = this.form.get('overdraftCompoundingPeriodUnit');
    const posting = this.form.get('overdraftPostingPeriodUnit');
    const open = this.allowOverdraft && this.isDepositAccount;

    if (open && this.canAdjust('overdraftInterestRate')) {
      rate.enable({ emitEvent: false });
    } else {
      rate.disable({ emitEvent: false });
    }

    if (open && this.canAdjust('overdraftInterestRatePeriodUnit')) {
      unit.enable({ emitEvent: false });
    } else {
      unit.disable({ emitEvent: false });
    }

    if (open && this.canAdjust('overdraftLimit')) {
      limit.enable({ emitEvent: false });
    } else {
      limit.disable({ emitEvent: false });
    }

    if (open && this.canAdjust('overdraftInterestCalculation')) {
      calculation.enable({ emitEvent: false });
    } else {
      calculation.disable({ emitEvent: false });
    }

    if (open && this.canAdjust('overdraftCompoundingPeriodUnit')) {
      compounding.enable({ emitEvent: false });
    } else {
      compounding.disable({ emitEvent: false });
    }

    if (open && this.canAdjust('overdraftPostingPeriodUnit')) {
      posting.enable({ emitEvent: false });
    } else {
      posting.disable({ emitEvent: false });
    }
  }

  // ── Display helpers ───────────────────────────────────────────────────────

  public get currencyCode(): string {
    return (
      this.productRecord?.currency ||
      this.currencyFormatter.getConfig().currencyCode ||
      'NGN'
    );
  }

  /** SCREAMING_SNAKE_CASE → "Screaming Snake Case", with a dash for empties. */
  public readableValue(value: any): string {
    if (value === null || value === undefined || value === '') return '-';
    return String(value)
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  public periodUnitLabel(value: string, plural = false): string {
    const labels: Record<string, string> = {
      DAY: 'day',
      WEEK: 'week',
      MONTH: 'month',
      YEAR: 'year',
    };
    const label = labels[value] || String(value || '').toLowerCase();
    return plural && label ? `${label}s` : label;
  }

  /** "% per month" — the unit panel inside the Interest Rate fields. */
  public rateUnitLabel(value: string): string {
    return `% per ${this.periodUnitLabel(value)}`;
  }

  /** "days" — the unit panel inside the Duration fields. */
  public durationUnitLabel(value: string): string {
    return this.periodUnitLabel(value, true);
  }

  public formatAmount(value: any): string {
    const amount = this.toNumber(value);
    if (!amount) return '-';
    return amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  public formatFeeAmount(value: any): string {
    const amount = this.toNumber(value);
    if (!amount) return '-';
    return `${this.currencyFormatter.getCurrencySymbol()}${amount.toLocaleString(
      undefined,
      { maximumFractionDigits: 2 },
    )}`;
  }

  /** Plain figure with a dash for empties, for the read-only day counts. */
  public plainValue(value: any): string {
    return value === null || value === undefined || value === ''
      ? '-'
      : String(value);
  }

  public percentValue(value: any): string {
    const amount = this.toNumber(value);
    return amount ? `${amount}%` : '-';
  }

  // Deposit Account read-only fields.

  public get minimumBalance(): string {
    return this.formatAmount(
      this.preview(
        this.field('minimumBalance', 'minimumOpeningBalance', 'openingBalance'),
        PREVIEW.minimumBalance,
      ),
    );
  }

  public get overdraftLimit(): string {
    return this.formatAmount(
      this.preview(this.field('overdraftLimit'), PREVIEW.overdraftLimit),
    );
  }

  public get transferLimit(): string {
    return this.formatAmount(
      this.preview(this.field('transferLimit'), PREVIEW.transferLimit),
    );
  }

  public get dailyTransferLimit(): string {
    return this.formatAmount(
      this.preview(
        this.field('dailyTransferLimit'),
        PREVIEW.dailyTransferLimit,
      ),
    );
  }

  public get dormancyThreshold(): string {
    return this.plainValue(
      this.preview(this.field('dormancyThreshold'), PREVIEW.dormancyThreshold),
    );
  }

  public get roundingMultiple(): string {
    if (!this.productRecord) return '';
    return this.plainValue(
      this.preview(this.field('roundingMultiple'), PREVIEW.roundingMultiple),
    );
  }

  public get collectionAccountNames(): string[] {
    if (!this.productRecord) return [];
    const accounts = this.field(
      'collectionAccounts',
      'fundSources',
      'savingsReference',
      'savingsReferences',
    );
    if (!Array.isArray(accounts) || !accounts.length) {
      return PREVIEW.collectionAccounts;
    }

    return accounts
      .map((account: any) =>
        account?.accountName ||
        account?.glAccountName ||
        account?.name ||
        account?.label,
      )
      .filter(Boolean);
  }

  private buildCollectionAccountOptions(record: any): Array<{ value: any; label: string }> {
    const accounts = record?.collectionAccounts
      ?? record?.fundSources
      ?? record?.savingsReference
      ?? record?.savingsReferences;
    if (!Array.isArray(accounts)) return [];
    return accounts.map((account: any) => ({
      value:
        account?.accountId
        ?? account?.glAccountId
        ?? account?.id
        ?? account?.accountName
        ?? account?.glAccountName
        ?? account?.name,
      label:
        account?.accountName
        ?? account?.glAccountName
        ?? account?.name
        ?? account?.label
        ?? '',
    })).filter(option => option.value !== undefined && option.label);
  }

  public get calculationBasisLabel(): string {
    if (!this.productRecord) return '';
    const calendarDays = this.field('isCalenderDays', 'isCalendarDays');
    return calendarDays === true
      ? $localize`:@@calender_days:Calendar Days`
      : $localize`:@@working_days_option:Working Days`;
  }

  public get weekendDaysLabel(): string {
    if (!this.productRecord) return '';
    const days = this.field('weekendDays');
    const selectedDays = Array.isArray(days) && days.length
      ? days
      : PREVIEW.weekendDays;
    return selectedDays.map((day: string) => this.readableValue(day)).join(', ');
  }

  public get compoundingLabel(): string {
    return this.readableValue(
      this.periodsLabel(
        this.field('compoundingPeriodUnit', 'compoundingFrequency') || 'MONTH',
      ),
    );
  }

  public get postingPeriodLabel(): string {
    return this.readableValue(
      this.periodsLabel(
        this.field('postingPeriodUnit', 'interestPostingPeriodUnit') || 'MONTH',
      ),
    );
  }

  public get interestPayoutLabel(): string {
    return this.readableValue(
      this.periodsLabel(
        this.field(
          'recurringInterestPayoutPeriodUnit',
          'fixedDepositInterestPayoutPeriodUnit',
          'interestPayoutPeriodUnit',
          'interestPayout',
        ) || 'MONTH',
      ),
    );
  }

  private normalizeRecurringContributionSource(value: string): string {
    return value === 'MANUAL' ? 'MANUAL_DEPOSIT' : value;
  }

  private normalizeRecurringCompletionAction(value: string): string {
    return value === 'USE_LINKED_SAVINGS_ACCOUNT'
      ? 'TRANSFER_TO_LINKED_SAVINGS_ACCOUNT'
      : value;
  }

  public get interestCalculationLabel(): string {
    return this.readableValue(
      this.field('interestCalculationType') || 'AVERAGE_BALANCE',
    );
  }

  // The API carries no overdraft-specific period units, so these mirror the
  // product's own schedule until it does.
  public get overdraftCompoundingLabel(): string {
    return this.readableValue(
      this.periodsLabel(
        this.field('overdraftCompoundingPeriodUnit', 'compoundingPeriodUnit') ||
          'MONTH',
      ),
    );
  }

  public get overdraftPostingPeriodLabel(): string {
    return this.readableValue(
      this.periodsLabel(
        this.field(
          'overdraftInterestPostingPeriodUnit',
          'postingPeriodUnit',
          'interestPostingPeriodUnit',
        ) || 'MONTH',
      ),
    );
  }

  public get overdraftInterestCalculationLabel(): string {
    return this.readableValue(
      this.field(
        'overdraftInterestCalculationType',
        'interestCalculationType',
      ) || 'AVERAGE_BALANCE',
    );
  }

  // Fixed Deposit read-only fields.

  public get rolloverBasisLabel(): string {
    const value = this.preview(
      this.field('rolloverBasis'),
      PREVIEW.rolloverBasis,
    );
    if (value === 'PRINCIPAL_AND_INTEREST') {
      return $localize`:@@principalPlusInterest:Principal + Interest`;
    }
    return this.readableValue(value);
  }

  public get gracePeriod(): string {
    return this.plainValue(
      this.preview(
        this.field(
          'recurringGracePeriodDays',
          'fixedDepositRolloverGracePeriodDays',
          'gracePeriod',
        ),
        this.isRecurringSavings
          ? PREVIEW.recurringGracePeriod
          : PREVIEW.gracePeriod,
      ),
    );
  }

  public get minimumHoldingPeriod(): string {
    return this.plainValue(
      this.preview(
        this.field('minimumHoldingPeriod'),
        PREVIEW.minimumHoldingPeriod,
      ),
    );
  }

  public get penaltyOnInterest(): string {
    return this.percentValue(
      this.preview(this.field('penaltyOnInterest'), PREVIEW.penaltyOnInterest),
    );
  }

  public get noticePeriod(): string {
    return this.plainValue(
      this.preview(this.field('noticePeriod'), PREVIEW.noticePeriod),
    );
  }

  // Recurring Savings read-only fields.

  public get savingsFrequencyLabel(): string {
    return this.readableValue(
      this.periodsLabel(
        this.preview(
          this.field('recurringSavingsFrequency', 'savingsFrequency'),
          PREVIEW.savingsFrequency,
        ),
      ),
    );
  }

  public get dayOfMonth(): string {
    return this.plainValue(
      this.preview(
        this.field(
          'recurringDayOfMonth',
          'savingsDayOfMonth',
          'dayOfMonth',
          'contributionDayOfMonth',
        ),
        PREVIEW.dayOfMonth,
      ),
    );
  }

  public get contributionSourceLabel(): string {
    return this.readableValue(
      this.preview(
        this.field('recurringContributionSource', 'contributionSource'),
        PREVIEW.contributionSource,
      ),
    );
  }

  public get bonusTypeLabel(): string {
    return this.readableValue(
      this.preview(
        this.field('recurringBonusType', 'bonusType'),
        PREVIEW.percentageType,
      ),
    );
  }

  public get bonusValue(): string {
    const type = this.preview(
      this.field('recurringBonusType', 'bonusType'),
      PREVIEW.percentageType,
    );
    const value = this.preview(
      this.field('recurringBonusValue', 'bonusValue'),
      PREVIEW.onePercent,
    );
    return type === 'PERCENTAGE'
      ? this.percentValue(value)
      : this.formatAmount(value);
  }

  public get earlyWithdrawalPenaltyTypeLabel(): string {
    return this.readableValue(
      this.preview(
        this.field(
          'recurringEarlyWithdrawalPenaltyType',
          'earlyWithdrawalPenaltyType',
        ),
        PREVIEW.percentageType,
      ),
    );
  }

  public get earlyWithdrawalPenaltyValue(): string {
    const type = this.preview(
      this.field(
        'recurringEarlyWithdrawalPenaltyType',
        'earlyWithdrawalPenaltyType',
      ),
      PREVIEW.percentageType,
    );
    const value = this.preview(
      this.field(
        'recurringEarlyWithdrawalPenaltyValue',
        'earlyWithdrawalPenaltyValue',
      ),
      PREVIEW.onePercent,
    );
    return type === 'PERCENTAGE'
      ? this.percentValue(value)
      : this.formatAmount(value);
  }

  public get minimumLockPeriod(): string {
    return this.plainValue(
      this.preview(
        this.field(
          'recurringEarlyWithdrawalMinimumLockPeriodDays',
          'minimumLockPeriodDays',
          'minimumLockPeriod',
          'maximumSavedPeriodDays',
        ),
        PREVIEW.minimumLockPeriod,
      ),
    );
  }

  public get missedContributionPenaltyTypeLabel(): string {
    return this.readableValue(
      this.preview(
        this.field(
          'recurringMissedContributionPenaltyType',
          'missedContributionPenaltyType',
        ),
        PREVIEW.percentageType,
      ),
    );
  }

  public get missedContributionPenaltyValue(): string {
    const type = this.preview(
      this.field(
        'recurringMissedContributionPenaltyType',
        'missedContributionPenaltyType',
      ),
      PREVIEW.percentageType,
    );
    const value = this.preview(
      this.field(
        'recurringMissedContributionPenaltyValue',
        'missedContributionPenaltyValue',
      ),
      PREVIEW.onePercent,
    );
    return type === 'PERCENTAGE'
      ? this.percentValue(value)
      : this.formatAmount(value);
  }

  public get atCompletionLabel(): string {
    const value = this.preview(
      this.field('recurringAtCompletionAction', 'glTemplate'),
      PREVIEW.atCompletion,
    );
    if (
      value === 'USE_LINKED_SAVINGS_ACCOUNT' ||
      value === 'TRANSFER_TO_LINKED_SAVINGS_ACCOUNT'
    ) {
      return $localize`:@@transferToLinkedSavings:Transfer to Linked Savings Account`;
    }
    return this.readableValue(value);
  }

  /** MONTH → "MONTHLY" so the read-only fields read as the designs do. */
  private periodsLabel(unit: string): string {
    const labels: Record<string, string> = {
      DAY: 'DAILY',
      WEEK: 'WEEKLY',
      MONTH: 'MONTHLY',
      YEAR: 'YEARLY',
    };
    return labels[unit] || unit;
  }

  // ── Fees & charges ────────────────────────────────────────────────────────

  public get productFees(): DepositFeeView[] {
    const charges: DepositProductFeeCharge[] =
      this.productRecord?.savingsProductCharges || [];
    if (!Array.isArray(charges) || !charges.length) {
      return this.previewFees;
    }

    return charges.map((charge) => {
      const rawEvent = charge?.event || 'DAILY';
      const isQuarterly = String(rawEvent).toUpperCase().includes('QUARTER');
      return {
        name: charge?.charge?.name || '-',
        value: this.formatFeeAmount(charge?.chargeAmount ?? charge?.value),
        minimum: this.formatFeeAmount(charge?.minValue),
        maximum: this.formatFeeAmount(charge?.maxValue),
        event: this.readableValue(rawEvent),
        eventType: isQuarterly ? 'QUARTERLY' : 'DAILY',
        eventTime: charge?.scheduleDetail || '11:59PM',
        quarters: [
          charge?.quarterOneMonth,
          charge?.quarterTwoMonth,
          charge?.quarterThreeMonth,
          charge?.quarterFourMonth,
        ].map((month) => this.monthShortLabel(month)),
      };
    });
  }

  /** Keep API month enums intact while presenting the compact Figma labels. */
  private monthShortLabel(month: unknown): string {
    const value = String(month ?? '').trim();
    if (!value) return '-';

    const normalized = value.toLowerCase();
    return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1, 3)}`;
  }

  /** TEMPORARY: the frames' three fee rows, until products carry charges. */
  private get previewFees(): DepositFeeView[] {
    return PREVIEW.fees.map((fee) => ({
      name: fee.name,
      value: this.formatFeeAmount(fee.amount),
      minimum: this.formatFeeAmount(fee.amount),
      maximum: this.formatFeeAmount(fee.amount),
      event: this.readableValue(fee.event),
      eventType: fee.event === 'QUARTERLY' ? 'QUARTERLY' : 'DAILY',
      eventTime: '11:59PM',
      quarters: ['Jan', 'Apr', 'Jul', 'Oct'],
    }));
  }

  // ── Amount inputs: comma formatting ───────────────────────────────────────

  /** Strip the comma formatting applied on blur so typing resumes cleanly. */
  public onAmountFocus(controlName: string): void {
    const raw = String(this.form.get(controlName).value ?? '');
    this.form
      .get(controlName)
      .setValue(raw.replace(/,/g, ''), { emitEvent: false });
  }

  public onAmountBlur(controlName: string): void {
    const amount = this.toNumber(this.form.get(controlName).value);
    this.form.get(controlName).setValue(
      amount
        ? amount.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : '',
      { emitEvent: false },
    );
  }

  // ── Live calculator: Deposit Account ──────────────────────────────────────

  public get calculatorInterestRate(): string {
    const rate = this.form?.get('interestRate')?.value;
    if (rate === null || rate === undefined || rate === '') return '-';
    return `${rate}% per ${this.periodUnitLabel(
      this.form.get('interestRatePeriodUnit').value,
    )}`;
  }

  private perYear(unit: string): number {
    return PERIODS_PER_YEAR[unit] ?? 12;
  }

  /** The nominal rate, annualised from whatever unit it is quoted in. */
  private get annualRate(): number {
    const rate = this.toNumber(this.form?.get('interestRate')?.value);
    return (
      (rate / 100) * this.perYear(this.form.get('interestRatePeriodUnit').value)
    );
  }

  private get compoundingPerYear(): number {
    return this.perYear(
      this.productRecord?.compoundingPeriodUnit ||
        this.productRecord?.compoundingFrequency ||
        'MONTH',
    );
  }

  /**
   * Interest accrued over a single posting period, populated from API response.
   */
  public get projectedInterest(): string {
    const value = this.depositProjectionResult?.projectedInterest;
    return value === null || value === undefined ? '-' : this.formatAmount(value);
  }

  // ── Live calculator: Fixed Deposit ────────────────────────────────────────

  public get durationDisplay(): string {
    const value = this.fixedProjectionResult?.durationInDays;
    return value === null || value === undefined ? '-' : String(value);
  }

  public get interestToMaturity(): string {
    const value = this.fixedProjectionResult?.interestToMaturity;
    return value === null || value === undefined ? '-' : this.formatAmount(value);
  }

  public get maturityValue(): string {
    const value = this.fixedProjectionResult?.maturityValue;
    return value === null || value === undefined ? '-' : this.formatAmount(value);
  }

  // ── Live calculator: Recurring Savings ────────────────────────────────────

  public get contributionPerPeriod(): string {
    const value = this.recurringProjectionResult?.contributionPerPeriod;
    return value === null || value === undefined ? '-' : this.formatAmount(value);
  }

  public get projectedInterestEarned(): string {
    const value = this.recurringProjectionResult?.projectedInterestEarned;
    return value === null || value === undefined ? '-' : this.formatAmount(value);
  }

  public get projectedBalanceAtCompletion(): string {
    const value = this.recurringProjectionResult?.projectedBalanceAtCompletion;
    return value === null || value === undefined ? '-' : this.formatAmount(value);
  }

  private toNumber(value: any): number {
    const parsed = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  public onSubmit() {
    if (!this.form.valid) {
      this.validatorService.CheckMessagesAndNotify(this.form.controls);
      return;
    }

    // getRawValue() so the active type's values survive even if a sibling
    // type's controls are disabled.
    const value = this.form.getRawValue();
    let payload: any = {
      savingsProductId: value.savingsProductId,
      productType: value.productType,
      savingsAccountType: this.isCorporate ? 'CORPORATE' : 'INDIVIDUAL',
      description: value.description || null,
      interestRate: value.interestRate === '' ? null : value.interestRate,
      interestRatePeriodUnit: value.interestRatePeriodUnit,
    };

    if (this.isDepositAccount) {
      payload.allowOverdraft = value.allowOverdraft;
      if (value.allowOverdraft) {
        payload.overdraftInterestRate =
          value.overdraftInterestRate === ''
            ? null
            : value.overdraftInterestRate;
        payload.overdraftInterestPeriodUnit =
          value.overdraftInterestRatePeriodUnit;
      }
    }

    if (this.isFixedDeposit) {
      payload = {
        clientId: this.data.id,
        savingsAccountType: this.isCorporate ? 'CORPORATE' : 'INDIVIDUAL',
        savingsProductId: Number(value.savingsProductId),
        purpose: value.description || null,
        interestRate:
          value.interestRate === '' || value.interestRate == null
            ? null
            : this.toNumber(value.interestRate),
        allowOverdraft: null,
        overdraftLimit: null,
        overdraftInterestRate: null,
        fixedDepositAmount: this.toNumber(value.amount) || null,
        fixedDepositDuration: this.toNumber(value.durationLength) || null,
        recurringTargetAmount: null,
        recurringDuration: null,
      };
    }

    if (this.isRecurringSavings) {
      payload = {
        clientId: this.data.id,
        savingsAccountType: this.isCorporate ? 'CORPORATE' : 'INDIVIDUAL',
        savingsProductId: Number(value.savingsProductId),
        purpose: value.description || null,
        interestRate:
          value.interestRate === '' || value.interestRate == null
            ? null
            : this.toNumber(value.interestRate),
        allowOverdraft: null,
        overdraftLimit: null,
        overdraftInterestRate: null,
        fixedDepositAmount: null,
        fixedDepositDuration: null,
        recurringTargetAmount: this.toNumber(value.targetAmount) || null,
        recurringDuration:
          this.toNumber(value.recurringDurationLength) || null,
      };
    }

    this.service
      .addSavingsAccount(
        this.isRecurringSavings || this.isFixedDeposit
          ? payload
          : { ...payload, clientId: this.data.id },
      )
      .subscribe((res) => {
        if (!res.successful) return;
        this.notif.success(res.message);
        this.dialogRef.close(true);
      });
  }

  public cancel() {
    this.dialogRef.close();
  }
}
